'use client'

import Link from 'next/link'
import { useState } from 'react'

import { UserType } from '@/entities/user'
import { useAirbridgeContext } from '@/shared/config'
import { cn, useScroll } from '@/shared/lib'
import { TextButton, Button, Separator, Icon, MobileLayout } from '@/shared/ui'

import Blog from '../asset/blog.svg'
import Instagram from '../asset/instagram.svg'
import YouTube from '../asset/youtube.svg'

type Props = {
  pageType: UserType
  className?: string
}

export const MobileFooter = ({ pageType, className }: Props) => {
  const airbridge = useAirbridgeContext()

  const [isOpenBusinessInfo, setIsOpenBusinessInfo] = useState(false)
  const { ref: businessInfoContentRef, scrollIntoView } =
    useScroll<HTMLButtonElement>()

  const handleOpenDeeplink = () => {
    airbridge.openDeeplink()
  }

  const handleOpenBusinessInfo = () => {
    const willExpand = !isOpenBusinessInfo

    setIsOpenBusinessInfo(willExpand)

    if (willExpand) {
      scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return (
    <footer className={cn('bg-white', className)}>
      <MobileLayout className="space-y-6">
        <ul className="mb-7.5 flex gap-4">
          <li>
            <TextButton weight="normal" size="small" underline asChild>
              <Link href={`/${pageType}/notice`}>공지사항</Link>
            </TextButton>
          </li>
          <li>
            <TextButton weight="normal" size="small" underline asChild>
              <Link href="/host/support/faq">호스트 자주 묻는 질문</Link>
            </TextButton>
          </li>
          <li>
            <TextButton weight="normal" size="small" underline asChild>
              <Link href="/guest/support/faq">게스트 자주 묻는 질문</Link>
            </TextButton>
          </li>
        </ul>

        <div>
          <h4 className="typo-label2 mb-1.5 font-semibold text-black">
            고객센터
          </h4>
          <ul
            className={cn(
              'typo-caption1 mb-1.5 flex space-x-1 font-normal text-gray-600',
              '*:flex',
              '*:not-last:after:ml-1 *:not-last:after:block *:not-last:after:text-gray-200 *:not-last:after:content-["ㅣ"]',
            )}
          >
            <li>
              <p>운영 시간 : 10:00~18:00</p>
            </li>
            <li>
              <p>대표 번호 : 1877-3670</p>
            </li>
          </ul>
          <p className="typo-caption1 mb-2 font-normal text-gray-500">
            주말·공휴일에는 전화 상담이 어려우니 1:1 문의하기를 이용해 주세요.
          </p>
          <Button
            variant="outline"
            size="small"
            className="w-[101px]"
            onClick={handleOpenDeeplink}
          >
            1:1 문의하기
            <Icon name="arrow-right" size={16} className="text-black" />
          </Button>
        </div>

        <div>
          <button
            ref={businessInfoContentRef}
            className="flex w-full items-center justify-between"
            onClick={handleOpenBusinessInfo}
            aria-expanded={isOpenBusinessInfo}
          >
            <h4 className="typo-label2 font-semibold text-black">사업자정보</h4>
            <Icon
              name="arrow-bottom"
              size={18}
              className={cn('text-black', isOpenBusinessInfo && 'rotate-180')}
            />
          </button>
          {isOpenBusinessInfo && (
            <p className={cn('typo-caption1 mt-1 text-gray-600')}>
              (주)스페이스브이
              <br />
              대표자 : 박형준
              <br />
              사업자 등록번호 : 886-81-01187
              <br />
              통신판매업 신고번호 : 2024-서울서초-0926
              <br />
              주소 : 서울특별시 서초구 나루터로 59, 6층
              <br />
              이메일 : 33m2@spacev.kr
              <br />
              제휴 문의 : business@spacev.kr
              <br />
              호스팅 서비스 제공 : Amazon Web Services Inc.
            </p>
          )}
          <ul className="mt-2 flex gap-4">
            <li className="h-4 leading-4">
              <TextButton
                color="gray"
                size="small"
                weight="normal"
                underline
                asChild
              >
                <Link href="/terms/using" target="_blank">
                  이용약관
                </Link>
              </TextButton>
            </li>
            <li className="h-4 leading-4">
              <TextButton
                color="gray"
                size="small"
                weight="normal"
                underline
                asChild
              >
                <Link href="/terms/privacy-policy" target="_blank">
                  개인정보 처리방침
                </Link>
              </TextButton>
            </li>
          </ul>
        </div>

        <ul className="flex gap-3.5">
          <li>
            <Link
              href="https://blog.naver.com/33m2_app"
              className="flex h-8 w-8 items-center justify-center"
              aria-label="33m2 블로그로 이동"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Blog />
            </Link>
          </li>
          <li>
            <Link
              href="https://www.instagram.com/33m2.co.kr"
              className="flex h-8 w-8 items-center justify-center"
              aria-label="33m2 인스타그램으로 이동"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Instagram />
            </Link>
          </li>
          <li>
            <Link
              href="https://www.youtube.com/@33m2.official"
              className="flex h-8 w-8 items-center justify-center"
              aria-label="33m2 유튜브로 이동"
              target="_blank"
              rel="noopener noreferrer"
            >
              <YouTube />
            </Link>
          </li>
        </ul>

        <Separator />

        <p className="typo-caption2 font-normal text-gray-500">
          (주)스페이스브이는 통신판매 중개자로서 통신판매의 당사자가 아닙니다.
          상품, 상품정보, 거래에 관한 의무와 책임은 계약 당사자에게 있습니다.
        </p>
      </MobileLayout>
    </footer>
  )
}
